import React, { useContext, useEffect, useState } from "react";
import { FirebaseContext } from "../contexts/FirebaseContext";
import { useCommonUI } from "../contexts/CommonUIContext";
import StatPickerDialog from "./StatPickerDialog";
import {
  STAT_TYPE_BY_KEY,
  computeStats,
  formatStat,
  isStatEmpty,
} from "../utils/taskListStats";
import {
  getStatSlots,
  setStatSlot,
  getStatCardsDisplayMode,
} from "../utils/statCardsSetting";
import {
  getHourlyRate,
  getAiHourlyRate,
  isAiInvoiceEnabled,
  getCurrency,
} from "../utils/billingRateSetting";

// Four swappable stat cards, mirroring Android's StatSlotsRow. Each card opens a
// picker (StatPickerDialog) to swap which of the 8 stat types it shows; a type
// can sit in only one slot. Visibility honors the Stat Cards Display Mode:
//   * HIDE           → never render
//   * ALWAYS_SHOW    → render all four
//   * SHOW_NON_EMPTY → all-or-nothing: hide the whole row only when EVERY slot
//                      is empty (brand-new project); otherwise show all four.
const StatCardsRow = ({ tasks }) => {
  const { getProjectStats } = useContext(FirebaseContext);
  const { openModal } = useCommonUI();

  const [slots, setSlots] = useState(getStatSlots());
  // Re-read the display mode on mount (the screen remounts on navigation, so it
  // refreshes after the Settings change).
  const displayMode = getStatCardsDisplayMode();

  const projectTasks = (tasks || []).filter((t) => !t.usedAsNote);
  const taskIds = projectTasks.map((t) => Number(t.id));
  const taskIdsKey = taskIds.slice().sort((a, b) => a - b).join(",");

  const [tracking, setTracking] = useState({
    totalSpentTime: 0,
    aiSpentTime: 0,
    sessionCount: 0,
    totalActions: 0,
  });

  useEffect(() => {
    let cancelled = false;
    getProjectStats(taskIdsKey ? taskIdsKey.split(",").map(Number) : []).then(
      (res) => {
        if (!cancelled) setTracking(res);
      }
    );
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskIdsKey]);

  if (displayMode === "HIDE") return null;

  const currency = getCurrency();
  const values = computeStats({
    tasks: projectTasks,
    totalSpentTime: tracking.totalSpentTime,
    aiSpentTime: tracking.aiSpentTime,
    sessionCount: tracking.sessionCount,
    totalActions: tracking.totalActions,
    hourlyRate: getHourlyRate(),
    aiHourlyRate: getAiHourlyRate(),
    aiInvoiceEnabled: isAiInvoiceEnabled(),
  });

  // SHOW_NON_EMPTY is all-or-nothing: collapse the row only when every slot is
  // empty; otherwise render all four (even the ones still reading 0).
  if (
    displayMode === "SHOW_NON_EMPTY" &&
    slots.every((key) => isStatEmpty(key, values[key]))
  ) {
    return null;
  }

  const openPicker = (slotIndex) => {
    const currentKey = slots[slotIndex];
    const usedByOtherKeys = slots.filter((_, i) => i !== slotIndex);
    openModal(
      <StatPickerDialog
        currentKey={currentKey}
        usedByOtherKeys={usedByOtherKeys}
        onSelect={(key) => setSlots(setStatSlot(slotIndex, key))}
      />
    );
  };

  return (
    <div className="stat-cards-grid" style={styles.grid}>
      {slots.map((key, index) => {
        const def = STAT_TYPE_BY_KEY[key];
        return (
          <button
            key={index}
            onClick={() => openPicker(index)}
            style={styles.card}
            title="Tap to change this stat"
          >
            {/* One row: label on the left, value on the right (no icon). */}
            <span style={styles.label}>{def.label}</span>
            <span style={styles.value}>
              {formatStat(key, values[key], currency)}
            </span>
          </button>
        );
      })}
    </div>
  );
};

const styles = {
  grid: {
    display: "grid",
    gap: "10px",
    marginBottom: "12px",
  },
  card: {
    backgroundColor: "var(--card)",
    borderRadius: "8px",
    padding: "10px 12px",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: "8px",
    minWidth: 0,
    border: "none",
    cursor: "pointer",
    textAlign: "left",
  },
  // Label takes the remaining space (ellipsizes if cramped); the value stays
  // fully visible on the right.
  label: {
    color: "var(--text-muted)",
    fontSize: "13px",
    flex: 1,
    minWidth: 0,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  value: {
    color: "var(--text)",
    fontSize: "15px",
    fontWeight: "bold",
    flexShrink: 0,
    whiteSpace: "nowrap",
  },
};

export default StatCardsRow;
